import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useI18n } from '../i18n/i18nContext';
import ProtectedRoute from './ProtectedRoute';
import CompleteRegistration from './CompleteRegistration';
import AuthCallback from './AuthCallback';
import PackagesPage from '../pages/PackagesPage';
import BookingPage from '../pages/BookingPage';
import BookingCalendarPage from '../pages/BookingCalendarPage';
import BookingConfirmPage from '../pages/BookingConfirmPage';
import AccountPage from '../pages/AccountPage';
import GiftCardPurchasePage from '../pages/GiftCardPurchasePage';
import { GiftCardBalancePage } from '../pages/GiftCardBalancePage';
import GiftCardPurchaseSuccessPage from '../pages/GiftCardPurchaseSuccessPage';
import CheckoutPage from '../pages/CheckoutPage';
import GuestBookingSuccessPage from '../pages/GuestBookingSuccessPage';
import BookingSuccessPage from '../pages/BookingSuccessPage';
import PurchaseConfirmationPage from '../pages/PurchaseConfirmationPage';
import VerifyEmailPage from '../pages/VerifyEmailPage';
import { InvitationResponsePage } from '../pages/InvitationResponsePage';
import { SpouseResponsePage } from '../pages/SpouseResponsePage';
import ServicePage from '../pages/ServicePage';
import PromotionsPage from '../pages/PromotionsPage';
import ContentPage from '../pages/ContentPage';
import PricingPage from '../pages/PricingPage';
import ContactPage from '../pages/ContactPage';
import GalleryPage from '../pages/GalleryPage';
import { userAPI } from '../services/api';
import { useUser } from '../contexts/UserContext';

const CheckoutProtectionWrapper: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth0();
  const { isLoadingUser } = useUser();
  const { t } = useI18n();
  const [userStatus, setUserStatus] = useState<{ 
    loading: boolean; 
    registrationCompleted: boolean | null;
  }>({
    loading: true,
    registrationCompleted: null
  });
  
  useEffect(() => {
    const checkRegistration = async () => {
      if (isAuthenticated && !isLoadingUser) {
        try {
          const profile = await userAPI.getProfile();
          setUserStatus({ 
            loading: false, 
            registrationCompleted: profile.data.registrationCompleted || false
          });
        } catch (error) {
          console.error('Error checking registration status:', error);
          setUserStatus({ 
            loading: false, 
            registrationCompleted: false
          });
        }
      } else if (!isAuthenticated && !isLoading) {
        setUserStatus({ loading: false, registrationCompleted: null });
      }
    };
    
    checkRegistration();
  }, [isAuthenticated, isLoadingUser, isLoading]);

  if (isLoading || isLoadingUser || userStatus.loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">{t.common.loading}</div>
      </div>
    );
  }

  if (isAuthenticated && userStatus.registrationCompleted === false) {
    const currentPath = window.location.pathname + window.location.search;
    localStorage.setItem('registration_return_to', currentPath);
    return <Navigate to="/complete-registration" replace />;
  }

  return <BookingConfirmPage />;
};

const LanguageRouter: React.FC = () => {
  const { language, setLanguage } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we need to redirect to add language prefix
  useEffect(() => {
    const pathname = location.pathname;
    const search = location.search;

    // Skip language prefix logic for special routes
    const specialRoutes = ['/callback'];
    if (specialRoutes.some(route => pathname.startsWith(route))) {
      return;
    }

    // Remove trailing slash (except for root paths like /ee/ which should become /ee)
    // This ensures URLs like /ee/pricing/ redirect to /ee/pricing
    if (pathname.length > 1 && pathname.endsWith('/')) {
      navigate(`${pathname.slice(0, -1)}${search}`, { replace: true });
      return;
    }

    // If no language prefix, redirect to default language
    if (!pathname.match(/^\/(en|ee|ru|fi)(\/|$)/)) {
      const langPrefix = language === 'et' ? 'ee' : language;
      navigate(`/${langPrefix}${pathname}${search}`, { replace: true });
    }

    // Extract language from URL and sync with context
    const urlLangMatch = pathname.match(/^\/(en|ee|ru|fi)(\/|$)/);
    if (urlLangMatch) {
      const urlLang = urlLangMatch[1] === 'ee' ? 'et' : urlLangMatch[1];
      if (urlLang !== language && (urlLang === 'en' || urlLang === 'et' || urlLang === 'ru' || urlLang === 'fi')) {
        setLanguage(urlLang as 'en' | 'et' | 'ru' | 'fi');
      }
    }
  }, [location.pathname, location.search, language, setLanguage, navigate]);

  return (
    <Routes>
      {/* Non-language-prefixed routes */}
      <Route path="/callback" element={<AuthCallback />} />
      
      {/* Language-prefixed routes */}
      <Route path="/:lang/*" element={<LanguagePrefixedRoutes />} />
      
      {/* Redirect root to default language */}
      <Route path="/" element={<Navigate to={`/${language === 'et' ? 'ee' : language}`} replace />} />

      {/* Redirect any unknown routes */}
      <Route path="*" element={<Navigate to={`/${language === 'et' ? 'ee' : language}`} replace />} />
    </Routes>
  );
};

const LanguagePrefixedRoutes: React.FC = () => {
  const { lang } = useParams<{ lang: string }>();
  const { setLanguage } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    // Validate and set language based on URL
    if (lang === 'en' || lang === 'ee' || lang === 'ru' || lang === 'fi') {
      const actualLang = lang === 'ee' ? 'et' : lang;
      setLanguage(actualLang as 'en' | 'et' | 'ru' | 'fi');
    } else {
      // Invalid language, redirect to Estonian
      navigate('/ee', { replace: true });
    }
  }, [lang, setLanguage, navigate]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<PackagesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/promotions" element={<PromotionsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/gallery" element={<GalleryPage />} />
      <Route path="/gift-card" element={<GiftCardPurchasePage />} />
      <Route path="/gift-card-success" element={<GiftCardPurchaseSuccessPage />} />
      <Route path="/check-balance" element={<GiftCardBalancePage />} />

      {/* Checkout Flow - Package Purchase (Protected) */}
      <Route 
        path="/checkout/:packageId"
        element={
          <ProtectedRoute>
            <CheckoutPage />
          </ProtectedRoute>
        }
      />
      
      {/* Checkout Flow - Cart Bookings (Protected for authenticated users) */}
      <Route 
        path="/checkout"
        element={<CheckoutProtectionWrapper />}
      />
      <Route 
        path="/purchase-confirmation"
        element={
          <ProtectedRoute>
            <PurchaseConfirmationPage />
          </ProtectedRoute>
        }
      />
      
      {/* Registration completion */}
      <Route 
        path="/complete-registration" 
        element={
          <ProtectedRoute requiresRegistration={false}>
            <CompleteRegistration />
          </ProtectedRoute>
        } 
      />
      
      {/* Service pages (public) */}
      <Route path="/services/:slug" element={<ServicePage />} />
      
      {/* Content pages (public) */}
      <Route path="/p/:slug" element={<ContentPage />} />
      
      {/* Booking routes (public - allow guest booking) */}
      <Route path="/book" element={<BookingPage />} />
      <Route path="/book/:serviceId" element={<BookingCalendarPage />} />
      <Route path="/book/:serviceId/confirm" element={<BookingConfirmPage />} />
      <Route path="/booking-success" element={<GuestBookingSuccessPage />} />
      <Route path="/booking-success/:orderId" element={<BookingSuccessPage />} />
      
      {/* Protected routes */}
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        }
      />
      
      {/* Email verification (public) */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      
      {/* Booking invitation response page (public) */}
      <Route path="/invitation/respond" element={<InvitationResponsePage />} />

      {/* Spouse invitation response page (public) */}
      <Route path="/spouse-response" element={<SpouseResponsePage />} />
      
      {/* Redirect any unknown routes to home with language prefix */}
      <Route path="*" element={<Navigate to={`/${lang}`} replace />} />
    </Routes>
  );
};

export default LanguageRouter;